import { Injectable, inject } from "@angular/core";
import { Observable, Subject, of } from "rxjs";
import { filter, switchMap } from "rxjs/operators";
import { ApiClientService } from "~app/common/services/api-client.service";
import { CreateSkogsvardTradslagPlantaCommand } from "../commands/skogsvard-tradslag-planta/CreateSkogsvardTradslagPlantaCommand";
import { DeleteSkogsvardTradslagPlantaCommand } from "../commands/skogsvard-tradslag-planta/DeleteSkogsvardTradslagPlantaCommand";
import { UpdateSkogsvardTradslagPlantaCommand } from "../commands/skogsvard-tradslag-planta/UpdateSkogsvardTradslagPlantaCommand";
import { AtgardSkogsvardTradslagPlanta, VKAtgardSkogsvardTradslagPlantorDTO } from "../models/skogsvard/skogsvard-models";
import { AllSkogsvardTradslagService } from "./all-skogsvard-tradslag.service";
import { AtgardMediatorService } from "./atgard-mediator.service";
import { KontraktCommandService } from "./kontrakt-command.service";

@Injectable()
export class SkogsvardTradslagService {
    private readonly api = inject(ApiClientService);
    private readonly kontraktCommandService = inject(KontraktCommandService);
    private readonly atgardMediatorService = inject(AtgardMediatorService);
    private readonly allSkogsvardTradslagService = inject(AllSkogsvardTradslagService);

    private readonly skogsvardTradslagPlantaRowsSrc = new Subject<AtgardSkogsvardTradslagPlanta[]>();
    private currentSkogsvardTradslagList: AtgardSkogsvardTradslagPlanta[] = [];

    get skogsvardTradslag$() {
        return this.skogsvardTradslagPlantaRowsSrc.asObservable();
    }

    constructor() {
        this.atgardMediatorService.atgard$
            .pipe(
                filter(x => (x.isForyngring || x.isPlantleverans) && !x.isForyngringSadd ),
                switchMap(atgard => this.getTradslagPlantorForSkogsvard(atgard.id)))
            .subscribe(tradslagPlantor => {
                this.currentSkogsvardTradslagList = tradslagPlantor;
                this.publishChange();
            });
    }

    updateSkogsvardTradslag(planta: AtgardSkogsvardTradslagPlanta) {
        const atgardGuid = this.atgardMediatorService.atgard.guid;

        if (!this.currentSkogsvardTradslagList.find(x => x.guid === planta.guid)) {
            const createCmd = new CreateSkogsvardTradslagPlantaCommand(atgardGuid, planta.guid);
            this.kontraktCommandService.update(createCmd);
            this.currentSkogsvardTradslagList.push(planta);
        }
        const cmd = new UpdateSkogsvardTradslagPlantaCommand(atgardGuid, planta);
        this.kontraktCommandService.update(cmd);

        this.publishChange();
    }

    deleteSkogsvardTradslag(guid: string) {
        const atgardGuid = this.atgardMediatorService.atgard.guid;
        const cmd = new DeleteSkogsvardTradslagPlantaCommand(atgardGuid, guid);
        this.kontraktCommandService.update(cmd);

        this.currentSkogsvardTradslagList = this.currentSkogsvardTradslagList.filter(x => x.guid !== guid);
        this.publishChange();
    }

    private getTradslagPlantorForSkogsvard(atgardId?: number): Observable<VKAtgardSkogsvardTradslagPlantorDTO[]> {
        if (atgardId) {
            return this.api.get<VKAtgardSkogsvardTradslagPlantorDTO[]>(`virkeskop2/atgard/skogsvard/${atgardId}/tradslagplantor`);
        } else { return of([]);}
    }

    private publishChange() {
        const listToPublish = [...this.currentSkogsvardTradslagList];
        this.skogsvardTradslagPlantaRowsSrc.next(listToPublish);
        const tradslagsList = listToPublish.map(x => x.tradslagId);
        this.allSkogsvardTradslagService.updateCacheForAtgard(this.atgardMediatorService.atgard.guid, tradslagsList);
    }

}
